import { ChevronRight, LayoutGrid } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useAllWorkspaceApps, useWorkspaceApps } from '../../../lib/app-shares';
import { useWorkspaces } from '../../../lib/workspaces';
import { Card, Typography } from '../ui';
import { AgentEntryRow } from './AgentEntryRow';
import styles from './FilesSummaryCard.module.css';

// The Apps tile — the fourth alongside Routines · Tasks · Files, opening the Apps screen.
//
// Scoped like the Files tile: account-wide by default, or one Space when given `workspaceId` +
// `workspaceSlug`, and it opens whichever screen it counted.
//
// **Cost, stated plainly:** the account-wide count is a fan-out — one read per Space — where the Files
// tile gets a single account-wide request. There is no cross-Space apps endpoint; `getWorkspaceArtifacts`
// is per-Space. The queries are shared through React Query with the `/apps` screen this opens (so the
// screen is free once the tile has paid) and the tile never blocks paint: it renders its title and
// chevron immediately and fills the count in. If Spaces-per-account grows enough for the fan-out to
// bite, the fix is an aggregate endpoint, not a cache tweak here.
export function AppsSummaryCard({
  pageName = 'agents',
  entryRow = false,
  workspaceId,
  workspaceSlug,
}: {
  pageName?: string;
  entryRow?: boolean;
  workspaceId?: string;
  workspaceSlug?: string;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();

  const workspacesState = useWorkspaces();
  // Only fan out for the account-wide tile; a Space's tile reads its own Space and nothing else.
  const allIds = useMemo(
    () => (workspaceId || workspacesState.status !== 'ready' ? [] : workspacesState.workspaces.map((w) => w.id).filter(Boolean)),
    [workspaceId, workspacesState],
  );
  const all = useAllWorkspaceApps(allIds);
  const one = useWorkspaceApps(workspaceId);

  const apps = workspaceId ? (one.status === 'ready' ? one.apps : []) : all.status === 'ready' ? all.apps : [];
  const spaces = workspaceId ? 0 : new Set(apps.map((a) => a.workspaceId).filter(Boolean)).size;

  const open = () => logPageClickBi({ page_name: pageName, button_name: 'open_apps' });
  const to = workspaceSlug ? `${basePath}/workspace/${encodeURIComponent(workspaceSlug)}/apps` : `${basePath}/apps`;
  // Spread as a record, the way the Files tile does it: Card's props do not declare `href`, even though
  // it forwards them to the `a` it renders.
  const href = { href: to } as Record<string, string>;

  // Until the read lands — and for an account with no apps — the prose blurb, not "0 apps · 0 spaces",
  // which states an emptiness the reader did not ask about. Same rule as the Files tile.
  const subtitle =
    apps.length === 0
      ? undefined
      : workspaceId
      ? t('apps.stats.workspace', { apps: apps.length })
      : t('apps.stats.account', { apps: apps.length, spaces });

  if (entryRow) {
    return <AgentEntryRow title={t('apps.section.title')} subtitle={subtitle} href={to} onClick={open} testId="agentsApps" />;
  }

  return (
    <Card component="a" interactive className={styles.card} {...href} onClick={open}>
      <div className={styles.body}>
        <span className={styles.title}>
          <LayoutGrid size={16} aria-hidden />
          <Typography variant="h4" color="strong" component="span" className={styles.titleText}>
            {t('apps.section.title')}
          </Typography>
        </span>
        <Typography variant="p2" color="secondary">
          {subtitle ?? t('apps.page.description')}
        </Typography>
      </div>
      <ChevronRight size={16} className={`${styles.chevron} dir-flip`} aria-hidden />
    </Card>
  );
}
